import { Col, Empty, Form, Menu, Select } from "antd";
import React, { useEffect, useState } from "react";
import {
  CommonButton,
  CommonFlexContainer,
  CommonInput,
  CommonSelect,
} from "../../../../../components/common/AtomCommonComponents";
import {
  CommonDropDownActions,
  CommonFormItemWithLabel,
  CommonModal,
  CommonStyledMenuForDropDown,
  CommonStyledTable,
} from "../../../../../components/common/MoleculesCommonComponent";
import DialogUtils from "../../../../../utils/DialogUtils";
import TextFormatUtils from "../../../../../utils/TextFormatUtils";
import BoxesService from "../../../../../services/api/BoxesService";
import ProductService from "../../../../../services/api/ProductService";
import CustomerService from "../../../../../services/api/CustomerService";
import Column from "antd/lib/table/Column";
import {
  StyledEditIcon,
  StyledOptionsIcon,
  StyledTrashIcon,
  StyledViewIcon,
} from "../../../../../components/icons/Icons";

import AddEditBoxProductsModal from "../../../../product/tabs/box/modals/AddEditBoxProductsModal";
import MedicaidService from "../../../../../services/api/MedicaidService";
import ValidationUtils from "../../../../../utils/ValidationUtils";

interface AddEditBoxModalProps {
  isAddEditBoxModalVisible: boolean;
  onCompleteModal: (data: any) => void;
  onCancelModal: () => void;
  editData?: any;
  subBoxData?: any;
  customerData?: any;
}

const AddEditBoxModal: React.FC<AddEditBoxModalProps> = (props) => {
  const [form] = Form.useForm();
  const [mode, setMode] = useState<string>("add");

  //Units
  const [units, setUnits] = useState([]);

  //products modal states
  const [productsData, setProductsData] = useState([]);
  const [boxData, setBoxData] = useState([]) as any;
  const [seasonalBoxData, setSeasonalBoxData] = useState([]) as any;
  const [selectedBox, setSelectedBox] = useState<any>(null); // For storing selected box data for editing
  const [editData, setEditData] = useState(null);
  const [forceRender, setForceRender] = useState(false);
  const [isAddEditSubBoxModalVisible, setIsAddEditSubBoxModalVisible] =
    useState<boolean>(false);
  const [viewData, setViewData] = useState<any>();
  const [isViewSubBoxModalVisible, setIsViewSubBoxModalVisible] =
    useState<boolean>(false);
  const [isAddEditProductModalVisible, setIsAddEditProductModalVisible] =
    useState<boolean>(false);
  const [reRun, setReRun] = useState<number>(0);
  const [customerData, setCustomerData] = useState<any>(null);

  const boxesService = new BoxesService();
  const productServie = new ProductService();
  const medicaidService = new MedicaidService();
  const customerService = new CustomerService();

  useEffect(() => {
    const initialize = async () => {
      let selectValue = await productServie.getSelect();
      if (selectValue) {
        setUnits(selectValue.units ? selectValue.units : []);
      }
      if (props.isAddEditBoxModalVisible) {
        if (props.editData) {
          setMode("edit");

          let customer = await customerService.getCustomerDetails(
            props.customerData
          );

          setCustomerData(customer);
          const { box_name } = props.editData.selectedBox;
          setProductsData(props.editData.selectedBox.products);
          form.setFieldsValue({
            box_name,
            price: props.editData.price,
          });
        } else {
          setMode("add");
          form.resetFields();
          setProductsData([]);
        }
      }
    };
    initialize();
  }, [props.isAddEditBoxModalVisible, form, props.editData]);

  const onEditAdmin = async (record: any) => {
    setEditData(record);
    setIsAddEditSubBoxModalVisible(true);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (
        (mode === "edit" && props.editData) ||
        props.editData.selectedBox?.box_id
      ) {
        const productNames = productsData
          .map((p: any) => p.product_id)
          .filter((n: string) => !!n);
        const finalProductData = await medicaidService.getCartDataFromNames(
          productNames
        );
        props.onCompleteModal({
          ...values,
          categories: finalProductData.categories,
          products: finalProductData.products,
          ...selectedBox,
          box_season_products: productsData,
        });
        DialogUtils.success("Box has been successfully updated.");
      } else {
        DialogUtils.error("Something went wrong!");
        return;
      }

      setBoxData([]);
      setProductsData([]);
      form.resetFields();
      props.onCompleteModal({});
    } catch (error) {
      return;
    }
  };

  const onCancelModalHandler = () => {
    props.onCancelModal();
    setProductsData([]);
    setBoxData([]);
    setEditData(null);
  };

  const onViewSubBox = (data: any) => {
    const boxName = form.getFieldValue("name") || "";
    if (boxName && data && data.season_name) {
      setViewData({
        ...data,
        season_name: `${boxName} - ${TextFormatUtils.capitalizeFirstLetter(
          data.season_name
        )}`.trim(),
      });
    } else {
      setViewData(data);
    }
    setIsViewSubBoxModalVisible(true);
  };

  const onDelete = (box_data: any) => {
    let updatedBoxData = boxData.filter(function (data: any) {
      return data.key ? data.key !== box_data : data._id !== box_data;
    });
    setBoxData(updatedBoxData);
  };

  const onEditTask = (data: any) => {
    data.index = productsData.findIndex((dataValue: any) => {
      return data.product_id === dataValue.product_id;
    });

    setEditData(data);
    setIsAddEditProductModalVisible(true);
  };

  const dropDownMenuItems = (record: any) => (
    <CommonStyledMenuForDropDown>
      <Menu.Item
        onClick={() => {
          onEditTask(record);
        }}
      >
        <CommonFlexContainer gap="10px">
          <StyledEditIcon size="1.5em" />
          <span>Edit</span>
        </CommonFlexContainer>
      </Menu.Item>
      <Menu.Item
        onClick={() =>
          DialogUtils.confirm(
            "Are you sure you want to delete this product?",
            () => onRemoveProducts(record)
          )
        }
      >
        <CommonFlexContainer gap="10px">
          <StyledTrashIcon />
          <span>Delete</span>
        </CommonFlexContainer>
      </Menu.Item>
    </CommonStyledMenuForDropDown>
  );

  const onRemoveProducts = (product_data: any) => {
    if (mode === "add") {
      const updatedProductList = productsData.filter(
        (item: any) => item.product_id !== product_data.product_id
      );
      setProductsData(updatedProductList);
    } else if (mode === "edit") {
      const updatedProductList = productsData.filter(
        (item: any) => item.product_id !== product_data.product_id
      );
      setProductsData(updatedProductList);
    }
  };

  const onCompleteProductModal = async (value: any, index: number) => {
    const fetchedProduct = await productServie.getProduct(value.product_id);
    const updatedProduct = {
      product_id: fetchedProduct._id,
      name: fetchedProduct.name,
      quantity: parseInt(value.quantity),
      unit: value.unit,
    };

    let updatedProducts: any[] = [];

    if (index === -1) {
      const exists = productsData.some(
        (item: any) => item._id === updatedProduct.product_id
      );
      if (!exists) {
        updatedProducts = [...productsData, updatedProduct];
      } else {
        updatedProducts = productsData.map((item: any) =>
          item._id === updatedProduct.product_id
            ? { ...item, quantity: parseInt(value.quantity), unit: value.unit }
            : item
        );
      }
    } else {
      let found = false;
      updatedProducts = productsData.map((item: any) => {
        if (item.product_id === updatedProduct.product_id) {
          found = true;
          return {
            ...item,
            name: updatedProduct.name,
            quantity: parseInt(value.quantity),
            unit: value.unit,
          };
        }
        return item;
      });

      if (!found) {
        // product_id changed: check if new product already exists elsewhere
        const existingIndex = updatedProducts.findIndex(
          (p: any) => p.product_id === updatedProduct.product_id
        );
        if (existingIndex !== -1) {
          // Merge quantities or override (choose override here)
          updatedProducts[existingIndex] = {
            ...updatedProducts[existingIndex],
            quantity: parseInt(value.quantity),
            unit: value.unit,
          };
          // Remove the old position (since product moved)
          if (existingIndex !== index) {
            updatedProducts = updatedProducts.filter(
              (_: any, i: number) => i !== index
            );
          }
        } else {
          // Replace the item at index with the new product definition
          updatedProducts = updatedProducts.map((item: any, i: number) =>
            i === index ? updatedProduct : item
          );
        }
      }
    }

    setProductsData(updatedProducts as any);
    setIsAddEditProductModalVisible(false);
    setEditData(null);
  };

  const onCancelProductModal = () => {
    setEditData(null);
    setIsAddEditProductModalVisible(false);
  };

  return (
    <CommonModal
      maskClosable={false}
      title={"Edit Box"}
      visible={props.isAddEditBoxModalVisible}
      onCancel={onCancelModalHandler}
      footer={[
        <CommonButton key="cancel" onClick={onCancelModalHandler}>
          Cancel
        </CommonButton>,
        <CommonButton
          key="save"
          color="white"
          background="primary"
          onClick={() => onSubmit()}
        >
          Save
        </CommonButton>,
      ]}
    >
      <Form form={form} validateTrigger="onChange" onFinish={onSubmit}>
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            return (
              <>
                <CommonFormItemWithLabel
                  label="Select Box"
                  name="selected_box_id"
                  rules={[
                    {
                      validator: (_: any, value: any) => {
                        if (value || props.editData?.selectedBox.box_id) {
                          form.setFieldsValue({
                            selected_box_id:
                              value || props.editData?.selectedBox.box_id,
                          });
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Please select a box"));
                      },
                    },
                  ]}
                >
                  <CommonSelect
                    disabled={true}
                    placeholder={
                      props.editData
                        ? props.editData?.selectedBox.box_name
                        : "Search or select a box"
                    }
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                  />
                </CommonFormItemWithLabel>
                <CommonFormItemWithLabel
                  label={"Price"}
                  name="price"
                  rules={[
                    { required: true, message: "Price is required." },
                    {
                      validator: (rule, value, callback) => {
                        return ValidationUtils.validateAndReplaceNumber(
                          value,
                          form,
                          "price",
                          "price"
                        );
                      },
                    },
                  ]}
                >
                  <CommonInput
                    maxLength={10}
                    placeholder="Input Price"
                    onInput={(e: any) => {
                      TextFormatUtils.upperText(e);
                    }}
                    color="primary"
                  />
                </CommonFormItemWithLabel>
                {customerData?.product_allergies?.length > 0 ? (
                  (() => {
                    const allergyNames = customerData?.product_allergies.map(
                      (data: any) => {
                        return data?.name?.trim();
                      }
                    );

                    return (
                      <div
                        style={{
                          marginBottom: 20,
                          padding: "14px 18px",
                          border: "2px solid #ff4d4f",
                          background: "#fff1f0",
                          borderRadius: 10,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            color: "#a8071a",
                            marginBottom: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          ⚠ Allergy Products
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#a8071a",
                            marginBottom: 10,
                            fontStyle: "italic",
                          }}
                        >
                          Reminder: The following items will be removed
                          automatically if found from the box due to allergen
                          contents.
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                          }}
                        >
                          {(() => {
                            const allergySet = new Set(
                              (allergyNames || []).map((n: string) =>
                                (n || "").trim().toLowerCase()
                              )
                            );

                            if (productsData?.length) {
                              const filtered = productsData.filter(
                                (p: any) =>
                                  !allergySet.has(
                                    (p?.name || "").trim().toLowerCase()
                                  )
                              );
                              if (filtered?.length !== productsData?.length) {
                                setProductsData(filtered);
                              }
                            }

                            return allergyNames.map((name: any) => (
                              <span
                                key={name}
                                style={{
                                  background: "#d9363e",
                                  color: "#fff",
                                  padding: "6px 14px",
                                  borderRadius: 24,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  letterSpacing: 0.5,
                                  textTransform: "capitalize",
                                }}
                              >
                                {name}
                              </span>
                            ));
                          })()}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <></>
                )}
                <div id="select">
                  <CommonFormItemWithLabel label="Products" name="products">
                    <CommonButton
                      background="primary"
                      color="white"
                      marginBottom="20px"
                      margintop="10px"
                      onClick={() => {
                        setIsAddEditProductModalVisible(true);
                      }}
                    >
                      Add Products
                    </CommonButton>
                    <CommonStyledTable
                      locale={{
                        emptyText: (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={"No Products"}
                          />
                        ),
                      }}
                      dataSource={productsData}
                    >
                      <Column
                        title="Name"
                        dataIndex="name"
                        key="name"
                        render={(value) => (value ? value : "-")}
                      />
                      <Column
                        title="Quantity"
                        align="right"
                        dataIndex="quantity"
                        key="quantity"
                        render={(value) => value}
                      />
                      <Column
                        title="Unit"
                        dataIndex="unit"
                        key="unit"
                        render={(value) => (value ? value : "-")}
                      />
                      <Column
                        title="Options"
                        key="_id"
                        render={(_, record: any) => (
                          <CommonDropDownActions
                            overlay={dropDownMenuItems(record)}
                            trigger={["click"]}
                            placement="bottomLeft"
                          >
                            <CommonButton justifyCenter transparent>
                              <StyledOptionsIcon size="24px" color="#203A20" />
                            </CommonButton>
                          </CommonDropDownActions>
                        )}
                      />
                    </CommonStyledTable>
                  </CommonFormItemWithLabel>
                </div>
              </>
            );
            return null;
          }}
        </Form.Item>
      </Form>
      <AddEditBoxProductsModal
        editData={editData}
        units={units}
        productsData={productsData}
        isAddEditBoxProductsModalVisible={isAddEditProductModalVisible}
        onCompleteModal={onCompleteProductModal}
        onCancelModal={onCancelProductModal}
      />
    </CommonModal>
  );
};

export default AddEditBoxModal;
